import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Paper, Typography, TextField, Button, Grid, Box, Alert,
  InputAdornment, IconButton, FormControlLabel, Switch, TableContainer,
  Table, TableHead, TableBody, TableRow, TableCell, Divider, MenuItem,
  Autocomplete, Chip
} from '@mui/material';
import { Add as AddIcon, Clear as ClearIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import instance from '../utils/axiosConfig.js';
import { CATEGORY_HIERARCHY, getQualitiesForCategory } from '../constants/categories';
import { getUnitsForCategory, SHIPPING_UNITS } from '../constants/units';
import { UNIT_LABELS_SINGULAR } from '../utils/units';
import { auth } from '../utils/auth';
const UNIT_OPTIONS = [
  { value: 'unidad', label: 'Unidades' },
  { value: 'kg', label: 'Kilogramos' },
  { value: 'lt', label: 'Litros' },
  { value: 'm2', label: 'Metros cuadrados' },
  { value: 'mt', label: 'Metros' },
  { value: 'cm', label: 'Centímetros' },
  { value: 'docena', label: 'Docenas' },
  { value: 'caja', label: 'Cajas' },
  { value: 'pack', label: 'Packs' },
  { value: 'hectarea', label: 'Hectáreas' }
];

const CreateProduct = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    code: '',
    description: '',
    category: '',
    availableAmount: '',
    basePrice: '',
    hidePrice: false,
    hideShipping: false,
    priceBreaks: [],
    imageUrls: [],
    unit: 'unidad',
    shippingUnit: 'unit',
    shippingFees: [],
    isFeatured: false
  });
  const [priceBreak, setPriceBreak] = useState({ minQuantity: '', price: '' });
  const [imageUrl, setImageUrl] = useState('');
  const [popularCategories, setPopularCategories] = useState([]);
  const [categoryWarning, setCategoryWarning] = useState(false);
  const [inputCategory, setInputCategory] = useState('');
  const [hasShippingFees, setHasShippingFees] = useState(true);
  const [specificCategoryWarning, setSpecificCategoryWarning] = useState({
    show: false,
    options: []
  });
  const [qualities, setQualities] = useState({});
  const [uploadingImages, setUploadingImages] = useState(false);
  const [canCreateFeatured, setCanCreateFeatured] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const fetchPopularCategories = async () => {
      try {
        const response = await instance.get('/api/products/popular-categories/creation?limit=20');
        const categories = response.data.map(cat => cat.category);
        setPopularCategories(categories);

        // Add some test categories if none exist
        if (categories.length === 0) {
          setPopularCategories(['Celulares', 'Cafeteras', 'Computadoras']);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        // Set test categories on error
        setPopularCategories(['Celulares', 'Cafeteras', 'Computadoras']);
      }
    };

    fetchPopularCategories();
  }, []);

  useEffect(() => {
    const fetchDistributorZones = async () => {
      try {
        const response = await instance.get('/api/distributors/profile');
        const zones = response.data.zones || [];
        
        // Initialize shipping fees for each zone
        const initialShippingFees = zones.map(zone => ({
          zone: zone,
          fees: {
            base: 0,
            unit: 'unit',
            pricePerUnit: 0
          }
        }));

        setFormData(prev => ({
          ...prev,
          shippingFees: initialShippingFees
        }));
      } catch (error) {
        console.error('Error fetching distributor zones:', error);
      }
    };

    fetchDistributorZones();
  }, []);

  useEffect(() => {
    if (formData.category) {
      const categoryQualities = getQualitiesForCategory(formData.category);
      if (categoryQualities) {
        // Initialize qualities with empty values
        const initialQualities = {};
        Object.keys(categoryQualities).forEach(key => {
          initialQualities[key] = '';
        });
        setQualities(initialQualities);
      } else {
        setQualities({});
      }
    }
  }, [formData.category]);

  useEffect(() => {
    const checkPermissions = async () => {  
      try {
        const user = auth.getUser();
        const plan = await instance.get(`api/plans/${user.subscription.planId}`);
        setCanCreateFeatured(plan.data.plan.permissions.featuredProductsLimit > 0);
      } catch (error) {
        console.error('Error checking permissions:', error);
      }
    };

    checkPermissions();
  }, []);

  // Get all categories flattened for autocomplete
  const getAllCategories = () => {
    let categories = [];
    Object.entries(CATEGORY_HIERARCHY).forEach(([mainCat, data]) => {
      categories.push(mainCat);
      Object.entries(data.subcategories).forEach(([subCat, detailedCats]) => {
        categories.push(subCat);
        categories = [...categories, ...detailedCats];
      });
    });
    return categories;
  };

  // Get child categories if any
  const getChildCategories = (category) => {
    // Check if it's a main category
    if (CATEGORY_HIERARCHY[category]) {
      return Object.keys(CATEGORY_HIERARCHY[category].subcategories);
    }
    
    // Check if it's a subcategory
    for (const [, data] of Object.entries(CATEGORY_HIERARCHY)) {
      if (data.subcategories[category]) {
        return data.subcategories[category];
      }
    }
    
    return [];
  };

  // Initialize filteredOptions with unique categories
  useEffect(() => {
    setFilteredOptions([...new Set(getAllCategories())]);
  }, []);

  const handleCategoryInputChange = (event, newInputValue) => {
    setInputCategory(newInputValue);
    setIsOpen(!!newInputValue);  // Only open if there's input
    
    // Get fresh list of categories and filter
    const allCategories = [...new Set(getAllCategories())]; // Remove duplicates here
    const filtered = newInputValue 
      ? allCategories.filter(category => 
          category.toLowerCase().startsWith(newInputValue.toLowerCase())
        )
      : allCategories;
    
    setFilteredOptions([...new Set(filtered)]); // And here
    
    setFormData(prev => ({
      ...prev,
      category: newInputValue
    }));
  };

  const handleCategoryChange = (event, newValue) => {
    if (newValue === null) return;

    const allCategories = getAllCategories();
    const categoryExists = allCategories.includes(newValue);
    const childCategories = getChildCategories(newValue);
    
    // Only set warning if category doesn't exist in our hierarchy
    setCategoryWarning(!categoryExists);
    
    // Show specific category warning if this category has children
    if (childCategories.length > 0) {
      setSpecificCategoryWarning({
        show: true,
        options: childCategories
      });
    } else {
      setSpecificCategoryWarning({
        show: false,
        options: []
      });
    }
    
    setFormData({
      ...formData,
      category: newValue
    });
  };

  const handleCategoryBlur = () => {
    if (inputCategory) {
      const allCategories = getAllCategories();
      const categoryExists = allCategories.includes(inputCategory);
      const childCategories = getChildCategories(inputCategory);
      
      // Only set warning if category doesn't exist in our hierarchy
      setCategoryWarning(!categoryExists);
      
      // Always check for children categories and show warning if they exist
      if (childCategories.length > 0) {
        setSpecificCategoryWarning({
          show: true,
          options: childCategories
        });
      } else {
        setSpecificCategoryWarning({
          show: false,
          options: []
        });
      }
      
      setFormData(prev => ({
        ...prev,
        category: inputCategory
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const dataToSubmit = {
        ...formData,
        hasPriceBreaks: formData.priceBreaks.length > 0,
        shippingFees: formData.hideShipping ? [] : formData.shippingFees,
        hideShippingFees: formData.hideShipping,
        qualities: Object.keys(qualities).length > 0 ? qualities : undefined
      };
      
      const response = await instance.post('/api/products', dataToSubmit);
      
      if (response.data) {
        navigate('/my-products');
      }
    } catch (error) {
      console.error('Error creating product:', error);
      setError(error.response?.data?.message || 'Error al crear el producto');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingImages(true);
    setError('');

    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const formData = new FormData();
        formData.append('image', file);

        const response = await instance.post('/api/upload/image', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        return response.data.url;
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      
      setFormData(prev => ({
        ...prev,
        imageUrls: [...prev.imageUrls, ...uploadedUrls]
      }));
    } catch (error) {
      console.error('Error uploading images:', error);
      setError('Error al subir las imágenes. Por favor, inténtelo de nuevo.');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleRemoveImage = (indexToRemove) => {
    setFormData(prev => {
      const newImageUrls = prev.imageUrls.filter((_, index) => index !== indexToRemove);
      return {
        ...prev,
        imageUrls: newImageUrls
      };
    });
  };

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>Crear nuevo producto</Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* Basic Info Section */}
            <Grid item xs={12}>
              <TextField
                fullWidth required size="small"
                label="Nombre del Producto"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
              />
            </Grid>

            {/* Category, Brand, Code Row */}
            <Grid item container xs={12} spacing={2}>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  freeSolo
                  options={filteredOptions}
                  value={formData.category || null}
                  inputValue={inputCategory}
                  onInputChange={handleCategoryInputChange}
                  onChange={handleCategoryChange}
                  onBlur={() => {
                    handleCategoryBlur();
                    setIsOpen(false);
                  }}
                  open={isOpen}
                  onClose={() => setIsOpen(false)}
                  size="small"
                  getOptionLabel={(option) => option.toString()}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      required
                      label="Categoría"
                      error={categoryWarning}
                      size="small"
                    />
                  )}
                />
                {categoryWarning && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Esta categoría no se encuentra presente en la base. 
                    Esto podría reducir la visibilidad de tu producto.
                    ¿Estás seguro que quieres proceder?
                  </Alert>
                )}
                {specificCategoryWarning.show && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Para mejor visibilidad, considera usar una categoría más específica:
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {specificCategoryWarning.options.map((option) => (
                        <Chip
                          key={option}
                          label={option}
                          size="small"
                          onClick={() => {
                            const newChildCategories = getChildCategories(option);
                            setInputCategory(option);
                            setFormData(prev => ({ ...prev, category: option }));
                            
                            // Check if the selected option has children
                            if (newChildCategories.length > 0) {
                              setSpecificCategoryWarning({
                                show: true,
                                options: newChildCategories
                              });
                            } else {
                              setSpecificCategoryWarning({
                                show: false,
                                options: []
                              });
                            }
                          }}
                          sx={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Box>
                  </Alert>
                )}
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth size="small"
                  label="Marca"
                  value={formData.brand}
                  onChange={(e) => setFormData({...formData, brand: e.target.value})}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth size="small"
                  label="Código"
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value})}
                />
              </Grid>
            </Grid>
            {getQualitiesForCategory(formData.category) && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Características específicas
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(getQualitiesForCategory(formData.category)).map(([key, config]) => (
                    <Grid item xs={12} sm={6} md={3} key={key}>
                      {config.type === 'select' ? (
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label={config.label}
                          value={qualities[key] || ''}
                          onChange={(e) => setQualities(prev => ({
                            ...prev,
                            [key]: e.target.value
                          }))}
                          required={config.required}
                        >
                          {config.options.map(option => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : (
                        <TextField
                          fullWidth
                          size="small"
                          label={config.label}
                          value={qualities[key] || ''}
                          onChange={(e) => setQualities(prev => ({
                            ...prev,
                            [key]: e.target.value
                          }))}
                          required={config.required}
                          InputProps={{
                            endAdornment: config.format && (
                              <InputAdornment position="end">
                                {config.format}
                              </InputAdornment>
                            )
                          }}
                          type={config.type}
                        />
                      )}
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth required multiline rows={3} size="small"
                label="Descripción"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              />
            </Grid>

            {/* Price Section */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>Precios (Opcional pero altamente recomendado)</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={formData.hidePrice}
                      onChange={(e) => setFormData({...formData, hidePrice: e.target.checked})}
                    />
                  }
                  label="Ocultar precios"
                />
                
              </Box>
              
              {!formData.hidePrice && (
                <>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth required size="small"
                        type="number"
                        label="Precio Base"
                        value={formData.basePrice}
                        onChange={(e) => {
                          const value = Math.max(0, Number(e.target.value));
                          setFormData({...formData, basePrice: value})
                        }}
                        inputProps={{ min: "0" }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth required size="small"
                        type="number"
                        label="Stock Disponible"
                        value={formData.availableAmount}
                        onChange={(e) => {
                          const value = Math.max(0, Number(e.target.value));
                          setFormData({...formData, availableAmount: value})
                        }}
                        inputProps={{ min: "0" }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        select
                        fullWidth
                        required
                        size="small"
                        label="Unidad"
                        value={formData.unit}
                        onChange={(e) => setFormData({...formData, unit: e.target.value})}
                      >
                        {(() => {
                          const units = getUnitsForCategory(formData.category);
                          return units.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ));
                        })()}
                      </TextField>
                    </Grid>
                  </Grid>

                  {/* Price Breaks */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Precios por cantidad
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField
                        size="small"
                        label="Cantidad mín."
                        type="number"
                        value={priceBreak.minQuantity}
                        onChange={(e) => {
                          const value = Math.max(0, Number(e.target.value));
                          setPriceBreak({...priceBreak, minQuantity: value})
                        }}
                        inputProps={{ min: "0" }}
                      />
                      <TextField
                        size="small"
                        label={`Precio por ${UNIT_LABELS_SINGULAR[formData.unit]}`}
                        type="number"
                        value={priceBreak.price}
                        onChange={(e) => {
                          const value = Math.max(0, Number(e.target.value));
                          setPriceBreak({...priceBreak, price: value})
                        }}
                        inputProps={{ min: "0" }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          if (priceBreak.minQuantity && priceBreak.price) {
                            setFormData({
                              ...formData,
                              priceBreaks: [...formData.priceBreaks, {
                                minQuantity: Number(priceBreak.minQuantity),
                                price: Number(priceBreak.price)
                              }]
                            });
                            setPriceBreak({ minQuantity: '', price: '' });
                          }
                        }}
                      >
                        <AddIcon />
                      </Button>
                    </Box>

                    {formData.priceBreaks.length > 0 && (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Cantidad</TableCell>
                              <TableCell>{(formData.unit === 'unidad' ? 'Precio por unidad' : 'Precio por ' + UNIT_LABELS_SINGULAR[formData.unit])}</TableCell>
                              <TableCell align="right">Acción</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {formData.priceBreaks.map((break_, index) => (
                              <TableRow key={index}>
                                <TableCell>{break_.minQuantity}+ {formData.unit}</TableCell>
                                <TableCell>${break_.price}</TableCell>
                                <TableCell align="right">
                                  <IconButton 
                                    size="small"
                                    onClick={() => setFormData({
                                      ...formData,
                                      priceBreaks: formData.priceBreaks.filter((_, i) => i !== index)
                                    })}
                                  >
                                    <ClearIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                </>
              )}
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>Costos de Envío</Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={!formData.hideShipping}
                        onChange={(e) => {
                          setFormData({...formData, hideShipping: !e.target.checked});
                          setHasShippingFees(e.target.checked);
                        }}
                      />
                    }
                    label="Incluir envío"
                  />
                </Box>
                
                {!formData.hideShipping && (
                  <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                    <TextField
                      select
                      fullWidth
                      required
                      size="small"
                      label="Unidad de Envío"
                      value={formData.shippingUnit}
                      onChange={(e) => {
                        const newUnit = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          shippingUnit: newUnit,
                          shippingFees: prev.shippingFees.map(fee => ({
                            ...fee,
                            fees: { ...fee.fees, unit: newUnit }
                          }))
                        }));
                      }}
                      sx={{ mb: 2 }}
                    >
                      {SHIPPING_UNITS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>

                    <Typography variant="subtitle2" gutterBottom>Tarifas por Zona</Typography>
                    <Grid container spacing={2}>
                      {formData.shippingFees.map((fee, index) => (
                        <Grid item xs={12} md={6} key={index}>
                          <Paper 
                            variant="outlined" 
                            sx={{ 
                              p: 1.5,
                              bgcolor: 'background.default'
                            }}
                          >
                            <Typography variant="subtitle2" gutterBottom>
                              Zona: {fee.zone}
                            </Typography>
                            <Grid container spacing={1}>
                              <Grid item xs={6}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  type="number"
                                  label="Tarifa Base"
                                  value={fee.fees.base}
                                  onChange={(e) => {
                                    const value = Math.max(0, Number(e.target.value || 0));
                                    const newFees = [...formData.shippingFees];
                                    newFees[index] = {
                                      ...newFees[index],
                                      fees: {
                                        ...newFees[index].fees,
                                        base: value
                                      }
                                    };
                                    setFormData(prev => ({ ...prev, shippingFees: newFees }));
                                  }}
                                  inputProps={{ min: "0" }}
                                  InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                  }}
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  type="number"
                                  label={`Precio por ${SHIPPING_UNITS.find(u => u.value === formData.shippingUnit)?.label.toLowerCase()}`}
                                  value={fee.fees.pricePerUnit}
                                  onChange={(e) => {
                                    const value = Math.max(0, Number(e.target.value || 0));
                                    const newFees = [...formData.shippingFees];
                                    newFees[index] = {
                                      ...newFees[index],
                                      fees: {
                                        ...newFees[index].fees,
                                        pricePerUnit: value
                                      }
                                    };
                                    setFormData(prev => ({ ...prev, shippingFees: newFees }));
                                  }}
                                  inputProps={{ min: "0" }}
                                  InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                )}
              </Box>
            </Grid>

            {/* Images Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>Imágenes</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  disabled={uploadingImages}
                >
                  {uploadingImages ? 'Subiendo...' : 'Subir Imágenes'}
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </Button>
              </Box>

              <Grid container spacing={1}>
                {formData.imageUrls.map((url, index) => (
                  <Grid item xs={6} sm={4} md={3} key={index}>
                    <Box sx={{ position: 'relative' }}>
                      <img
                        src={url}
                        alt={`Product ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100px',
                          objectFit: 'cover',
                          borderRadius: '4px'
                        }}
                      />
                      <IconButton
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          bgcolor: 'background.paper',
                          '&:hover': { bgcolor: 'background.paper' }
                        }}
                        onClick={() => handleRemoveImage(index)}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            {/* Add Featured Product switch if distributor has permission */}
            {canCreateFeatured && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isFeatured}
                      onChange={(e) => setFormData({
                        ...formData,
                        isFeatured: e.target.checked
                      })}
                    />
                  }
                  label="Producto Destacado"
                />
              </Grid>
            )}

            {error && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                sx={{ mt: 2 }}
                disabled={loading}
              >
                {loading ? 'Creando...' : 'Crear Producto'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default CreateProduct; 